import { ApiResponse } from '../types';
import { authStore } from '../store/auth-store';

// 全局 API 配置
// 为了支持在同一个镜像提供前后端服务，使用空字符串作为 Base URL
const API_BASE_URL = '';

/**
 * 跨系统请求统一封装
 * 要求：必须捕获所有 Promise 异常并提供反馈
 * @param isFormData 为 true 或 body 为 FormData 时不设置 Content-Type，便于 multipart boundary
 * @param opts.envelope 为 true 时返回完整 JSON（含 page/total），不剥 data；用于分页列表
 */
export async function request<T>(
    url: string,
    options?: RequestInit & { envelope?: boolean },
    isFormData?: boolean
): Promise<T> {
    const envelope = options?.envelope === true;
    // 避免把自定义字段传给 fetch
    const { envelope: _envelopeIgnored, ...fetchOptions } = options || {};
    try {
        // 统一使用 Bearer JWT，禁止拼接 api_key 查询参数
        const urlObj = new URL(API_BASE_URL + url, window.location.origin);
        const token = authStore.token || localStorage.getItem('auth_token');

        const useForm =
            isFormData === true ||
            (typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData);
        const baseHeaders: Record<string, string> = {};
        if (!useForm) {
            baseHeaders['Content-Type'] = 'application/json';
        }

        const res = await fetch(urlObj.toString(), {
            ...fetchOptions,
            headers: {
                ...baseHeaders,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(fetchOptions.headers as Record<string, string> | undefined),
            }
        });

        if (!res.ok) {
            if (res.status === 401 || res.status === 403) {
                authStore.logout();
                window.location.href = '/login';
                throw new Error('鉴权失败或登录过期，请重新登录');
            }
            let detail = `HTTP ${res.status}`;
            const ct = res.headers.get('content-type') ?? '';
            try {
                const raw = await res.text();
                if (ct.includes('application/json')) {
                    const j = JSON.parse(raw) as { error?: string; message?: string; msg?: string };
                    detail = j.message || j.msg || j.error || raw || detail;
                } else if (raw.trim()) {
                    detail = raw.trim();
                }
            } catch {
                /* keep detail */
            }
            throw new Error(detail);
        }

        const json: ApiResponse<T> = await res.json();

        // 如果没有标准的 ApiResponse 包装（取决于后端实际返回），
        // 或者后端发生业务层错误：
        // 管理端约定 code=0；部分接口（如车端 car-api）使用 code=200
        if (json.code !== undefined && json.code !== 0 && json.code !== 200) {
            const j = json as ApiResponse<T> & { msg?: string };
            const errText = j.message || j.msg || '系统请求失败';
            console.error(`业务异常 [${json.trace_id}]:`, errText);
            throw new Error(errText);
        }

        if (envelope) {
            return json as unknown as T;
        }

        // 后端某些原生 Gin 返回如果没有统一 code 包装，可能直接返回数据体
        // 这里兼容两种形式：如果有 data 则返回 data，否则直接返回 json 本身
        return (json.data !== undefined) ? json.data : (json as unknown as T);
    } catch (err: unknown) {
        const errorMsg = err instanceof Error ? err.message : '未知网络错误';
        console.error('Request failed:', errorMsg);
        // 强制 UI 提供反馈
        alert(`系统请求异常: ${errorMsg}`);
        throw err;
    }
}
